import type { IProbUtil } from '@slot-nx/prob'
import type { SlotPotAction, SlotPotCollectMode, SlotPotLevelRuleMap, SlotPotRecord } from './types'

export interface ISlotPotStoreAdapter {
  getRecord(): SlotPotRecord
  setRecord(record: SlotPotRecord): void
  /** Load persisted pot state into the backing store (e.g. sessionStorage → Zustand). */
  getPotStatusFromSessionStorage(): Promise<void>
}

function ruleMapKeyBounds(map: SlotPotLevelRuleMap): { minLevel: number, maxLevel: number } {
  const keys = Object.keys(map).map(Number)
  if (keys.length === 0) {
    throw new Error('SlotPot level rule map must not be empty')
  }
  return { minLevel: Math.min(...keys), maxLevel: Math.max(...keys) }
}

function hasWeightedNextLevels(rule: { nextLevelProbMap?: Readonly<Record<number, number>> }): boolean {
  const m = rule.nextLevelProbMap
  return m != null && Object.keys(m).length > 0
}

export class SlotPot {
  private levelRuleMap: SlotPotLevelRuleMap = {}
  private maxLevel = 0
  private minLevel = 0

  /**
   * @param id - Key in {@link PlayerPotInfo.potRecordMap}; use to distinguish multiple pots in one game.
   * @param storeAdapter - External state backend (e.g. Zustand + sessionStorage write-through).
   *   `getRecord` is the single source of truth; `setRecord` must persist atomically.
   * @param collectMode - Level-up roll strategy; see {@link SlotPotCollectMode}.
   *   Defaults to `threshold` so existing single-pot games are unaffected.
   */
  constructor(
    readonly id: string,
    private readonly probUtil: IProbUtil,
    levelRuleMap: SlotPotLevelRuleMap,
    public readonly storeAdapter: ISlotPotStoreAdapter,
    private readonly collectMode: SlotPotCollectMode = 'threshold',
  ) {
    this.setLevelRuleMap(levelRuleMap)
    const initial = storeAdapter.getRecord()
    if (initial.level > this.maxLevel || initial.level < this.minLevel) {
      throw new Error(
        `Slot record level is out of range, should be ${this.minLevel}-${this.maxLevel}, but was ${initial.level}`,
      )
    }
  }

  get currentRule() {
    const { level } = this.storeAdapter.getRecord()
    return this.levelRuleMap[level]
  }

  setLevelRuleMap(levelRuleMap: SlotPotLevelRuleMap): void {
    this.levelRuleMap = levelRuleMap
    const { minLevel, maxLevel } = ruleMapKeyBounds(levelRuleMap)
    this.minLevel = minLevel
    this.maxLevel = maxLevel
  }

  setRecord(record: SlotPotRecord): void {
    this.storeAdapter.setRecord({ ...record })
  }

  getPotStatusFromSessionStorage(): Promise<void> {
    return this.storeAdapter.getPotStatusFromSessionStorage()
  }

  collect(count = 1): [boolean, SlotPotAction | null] {
    if (this.collectMode === 'scaling') {
      return this.collectScaling(count)
    }

    const curr = this.storeAdapter.getRecord()
    if (curr.level >= this.maxLevel) {
      return [false, null]
    }

    const next: SlotPotRecord = { ...curr, collectNum: curr.collectNum + count }
    this.storeAdapter.setRecord(next)

    const rule = this.levelRuleMap[next.level]
    if (next.collectNum >= rule.collectThreshold && this.probUtil.isWin(rule.levelUpProb)) {
      return [true, this.levelUp()]
    }

    return [false, null]
  }

  /**
   * Backend-parity collect: each symbol is rolled individually with a
   * probability that scales by the already-accumulated `collectNum`, and the
   * counter only advances when the roll loses.
   *
   * Returns the *net* level change across all `count` symbols, so a spin that
   * levels up more than once still yields a single `from`→`to` action.
   */
  private collectScaling(count: number): [boolean, SlotPotAction | null] {
    const startLevel = this.storeAdapter.getRecord().level
    let leveledUp = false

    for (let i = 0; i < count; i++) {
      const curr = this.storeAdapter.getRecord()
      if (curr.level >= this.maxLevel) {
        break
      }

      const rule = this.levelRuleMap[curr.level]
      const levelUpProb = Math.min(rule.levelUpProb * curr.collectNum, 1)
      if (this.probUtil.isWin(levelUpProb)) {
        this.levelUp()
        leveledUp = true
        continue
      }

      if (curr.collectNum < rule.collectThreshold) {
        this.storeAdapter.setRecord({ ...curr, collectNum: curr.collectNum + 1 })
      }
    }

    if (!leveledUp) {
      return [false, null]
    }
    return [true, { from: startLevel, to: this.storeAdapter.getRecord().level }]
  }

  private levelUp(): SlotPotAction {
    const curr = this.storeAdapter.getRecord()
    const rule = this.levelRuleMap[curr.level]
    const nextLevel = hasWeightedNextLevels(rule)
      ? this.probUtil.drawFromWeightMap(rule.nextLevelProbMap!)
      : curr.level + 1
    return this.forceLevelUp(nextLevel)
  }

  forceLevelUp(nextLevel: number): SlotPotAction {
    if (!(nextLevel in this.levelRuleMap)) {
      throw new Error(`cant not find level rule of ${nextLevel}`)
    }
    const curr = this.storeAdapter.getRecord()
    const action: SlotPotAction = { from: curr.level, to: nextLevel }
    this.storeAdapter.setRecord({ level: nextLevel, collectNum: 0 })
    return action
  }

  clear(): this {
    this.storeAdapter.setRecord({ level: this.minLevel, collectNum: 0 })
    return this
  }
}
